'use client';

import LandingLayout from '@/components/LandingLayout';
import { useAuth } from '@/contexts/AuthContextNext';
import {
  CalculatorIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  PhoneIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { trackButtonClick, trackPageView, trackPhoneClick } from '../../lib/analytics';
import { trackEvent } from '../../lib/mixpanel';
import { getPlanDisplayPrice, PRICING_PLANS } from '../../types/pricing';

function formatCurrency(number: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(number);
}

function formatPriceAmount(amount: number): string {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export default function PricingPage() {
  useEffect(() => {
    trackPageView('Pricing Page', undefined, { funnel_step: 'pricing' });
  }, []);

  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };

  // ROI Calculator state
  const [missedCalls, setMissedCalls] = useState(15);
  const [avgDeal, setAvgDeal] = useState(500);

  // Auto-select the correct plan based on call volume using actual callsPerMonth from pricing plans
  const getRecommendedPlan = (calls: number) => {
    // Find the most affordable plan that can handle the call volume
    for (const plan of PRICING_PLANS) {
      if (calls <= plan.callsPerMonth) {
        return plan;
      }
    }
    // If calls exceed all plans, return the highest tier
    return PRICING_PLANS[PRICING_PLANS.length - 1];
  };

  const recommendedPlan = getRecommendedPlan(missedCalls);
  const recommendedDisplayPrice = getPlanDisplayPrice(recommendedPlan, pricingContext);
  const annualCost = recommendedDisplayPrice.pricePerYearUSD;

  const roi = {
    monthlyLoss: missedCalls * avgDeal * 0.3, // 30% conversion rate
    annualLoss: missedCalls * avgDeal * 0.3 * 12,
    greetCost: annualCost,
    savings: (missedCalls * avgDeal * 0.3 * 12) - annualCost
  };

  const testimonials = [
    {
      name: "Sarah Johnson",
      business: "Sunshine Salon",
      avatar: "SJ",
      rating: 5,
      text: "Greet pays for itself. We book 15+ more appointments every month just from calls we used to miss. That's an extra $3,000 in revenue.",
      savings: "$3,000/month"
    },
    {
      name: "Mike Rodriguez", 
      business: "Rodriguez Plumbing",
      avatar: "MR",
      rating: 5,
      text: "Best investment I've made. Greet captures emergency calls at night and weekends - that's when we make the most money. ROI is incredible.",
      savings: "$5,000/month"
    },
    {
      name: "Dr. Emily Chen",
      business: "Downtown Dental",
      avatar: "EC",
      rating: 5,
      text: "We were spending $3,200/month on a part-time receptionist. Greet does more for $80/month and never calls in sick.",
      savings: "$3,120/month"
    }
  ];

  const faqs = [
    {
      question: "How does this compare to hiring a receptionist?",
      answer: "A part-time receptionist costs $20,000-$35,000/year plus benefits. Greet costs $599-$2,399/year and works 24/7 without sick days, vacations, or breaks."
    },
    {
      question: "What's included in the free trial?",
      answer: "Everything! Full access to all features for 7 days. We'll even help you set up your FAQ and phone number."
    },
    {
      question: "Can I change plans anytime?",
      answer: "Yes! Upgrade or downgrade anytime. Changes take effect immediately and we'll pro-rate your billing automatically."
    },
    {
      question: "What happens if I go over my call limit?",
      answer: "We'll never cut you off. Overage calls are handled like any other call. We'll notify you before you hit your limit so you can upgrade if needed."
    },
    {
      question: "Do you offer discounts for annual billing?",
      answer: "Yes! Pay annually and save 20% (coming soon). We also offer discounts for multiple locations and non-profits."
    },
    {
      question: "Is spam protection included in all plans?",
      answer: "Yes! All plans include automatic spam detection and manual blocking at no extra cost. Your calls are protected by our database of 10.5M+ known spam numbers, and you can manually block any number anytime."
    }
  ];

  const features = [
    {
      category: "Core Features",
      items: [
        { name: "AI Call Answering", starter: true, growth: true, pro: true },
        { name: "FAQ Responses", starter: true, growth: true, pro: true },
        { name: "Call Transcripts", starter: true, growth: true, pro: true },
        { name: "Email Summaries", starter: true, growth: true, pro: true },
        { name: "Spam Detection & Blocking", starter: true, growth: true, pro: true },
        { name: "Basic Lead Qualification", starter: true, growth: true, pro: true }
      ]
    },
    {
      category: "Advanced Features", 
      items: [
        { name: "Appointment Scheduling", starter: false, growth: true, pro: true },
        { name: "Remove Greet Branding", starter: false, growth: true, pro: true },
        { name: "Multi-language Support", starter: false, growth: "5 languages", pro: "10+ languages" },
        { name: "Advanced Analytics", starter: false, growth: true, pro: true },
        { name: "Priority Support", starter: false, growth: true, pro: true },
        { name: "CRM Integrations", starter: false, growth: false, pro: true }
      ]
    },
    {
      category: "Usage Limits",
      items: [
        { name: "Monthly Calls", starter: "50", growth: "100", pro: "500" },
        { name: "Phone Numbers", starter: "1", growth: "1", pro: "3" },
        { name: "AI Personalities", starter: "3", growth: "10", pro: "All" },
        { name: "Support", starter: "Email", growth: "Priority", pro: "Dedicated" }
      ]
    }
  ];

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-16 relative">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Choose Your Plan
              </span>
              <br />
              <span className="text-gray-900">Start Free for 7 Days</span>
            </h1>
            

            {/* Live Demo CTA */}
            <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md mx-auto mb-12 border-2 border-blue-200">
              <h3 className="font-bold text-gray-900 mb-2">Try Before You Buy</h3>
              <p className="text-gray-600 text-sm mb-4">Call our AI receptionist right now - it's trained on Greet's business!</p>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'pricing_page_hero')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all inline-flex items-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                (415) 877-3166
              </a>
            </div>
            <p className="text-center text-gray-600 max-w-xl mx-auto mb-8">
              Want the full cost breakdown vs traditional answering services?{' '}
              <Link href="/ai-receptionist-pricing" className="text-blue-700 font-semibold hover:text-blue-900">
                See AI receptionist pricing
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See Your ROI in Real Numbers
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Calculate how much money you're losing from missed calls and how much Greet can save you
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Calculator */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-blue-200">
              <div className="flex items-center gap-3 mb-6">
                <CalculatorIcon className="h-8 w-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">ROI Calculator</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="missedCalls">
                    How many calls do you miss per month?
                  </label>
                    <input
                      id="missedCalls"
                      type="range"
                      min="5"
                      max="300"
                      step="5"
                      value={missedCalls}
                      onChange={(e) => setMissedCalls(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>5</span>
                      <span className="font-bold text-gray-900">{missedCalls} calls</span>
                      <span>300+</span>
                    </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="avgDeal">
                    Average value per customer
                  </label>
                  <input
                    id="avgDeal"
                    type="range"
                    min="50"
                    max="2000"
                    step="10"
                    value={avgDeal}
                    onChange={(e) => setAvgDeal(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>{formatCurrency(50)}</span>
                    <span className="font-bold text-gray-900">${avgDeal}</span>
                    <span>{formatCurrency(2000)}+</span>
                  </div>
                </div>

                <div>
                  <p className="block text-sm font-medium text-gray-700 mb-2">
                    Recommended plan for your call volume
                  </p>
                  <div className="w-full border border-blue-300 bg-blue-50 rounded-lg px-4 py-3 cursor-default">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-blue-900 capitalize">{recommendedPlan.name}</span>
                        <span className="text-blue-700 ml-2">
                          - ${recommendedDisplayPrice.pricePerMonthUSD}/month
                        </span>
                      </div>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                        Recommended
                      </span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">
                      Perfect for up to {recommendedPlan.callsPerMonth} calls/month
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">

              <div className="bg-green-50 border border-green-300 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-green-900 mb-4 flex items-center">
                  <CheckCircleIcon className="h-6 w-6 mr-2" />
                  Your Savings with Greet
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-green-700">Greet annual cost</span>
                    <span className="font-bold text-green-900">{formatCurrency(roi.greetCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Recovered revenue</span>
                    <span className="font-bold text-green-900">{formatCurrency(roi.annualLoss)}</span>
                  </div>
                  <hr className="border-green-200" />
                  <div className="flex justify-between">
                    <span className="text-green-700 font-semibold">Net profit increase</span>
                    <span className="font-bold text-green-900 text-xl">{formatCurrency(roi.savings)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">ROI: {Math.round((roi.savings / roi.greetCost) * 100).toLocaleString()}%</h4>
                <p className="text-gray-700 text-sm">
                  Greet pays for itself in just {Math.ceil(roi.greetCost / roi.monthlyLoss)} {Math.ceil(roi.greetCost / roi.monthlyLoss) === 1 ? 'month' : 'months'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              All plans include a 7-day free trial
            </p>
            
            {/* Billing toggle */}
            <div className="flex items-center justify-center">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`mr-3 font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`ml-3 font-medium transition-colors ${billingPeriod === 'annual' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Annual
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full hover:bg-green-200 transition-colors"
              >
                Save 20%
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PRICING_PLANS.map((plan) => {
              const isPopular = plan.id === 'growth';
              const displayPrice = getPlanDisplayPrice(plan, pricingContext);
              const showSalePrice = displayPrice.showSale;
              const currentPrice = billingPeriod === 'annual'
                ? displayPrice.pricePerYearUSD
                : displayPrice.pricePerMonthUSD;
              const basePrice = billingPeriod === 'annual'
                ? displayPrice.basePricePerYearUSD
                : displayPrice.basePricePerMonthUSD;
              const monthlyEquivalent = displayPrice.pricePerYearUSD / 12;
              
              return (
                <div 
                  key={plan.id} 
                  className={`relative rounded-3xl ${
                    isPopular 
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-2xl scale-105' 
                      : 'bg-white border-2 border-gray-200 shadow-lg'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      ⭐ Most Popular
                    </div>
                  )}
                  {showSalePrice && displayPrice.label && (
                    <div className={`absolute ${isPopular ? 'top-6' : '-top-4'} right-6 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold shadow-sm`}>
                      {displayPrice.label}
                    </div>
                  )}

                  <div className="p-8">
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <p className={`text-sm mb-6 ${isPopular ? 'text-blue-100' : 'text-gray-600'}`}>
                      {plan.subtitle}
                    </p>
                    
                    <div className="mb-8">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        {showSalePrice && (
                          <span className={`text-xl font-semibold line-through ${isPopular ? 'text-blue-200' : 'text-gray-400'}`}>
                            ${formatPriceAmount(basePrice)}
                          </span>
                        )}
                        <span className="text-4xl font-bold">
                          ${formatPriceAmount(currentPrice)}
                        </span>
                        <span className={`ml-2 ${isPopular ? 'text-blue-200' : 'text-gray-600'}`}>
                          /{billingPeriod === 'annual' ? 'year' : 'month'}
                        </span>
                      </div>
                      {showSalePrice && (
                        <p className={`text-sm mt-1 ${isPopular ? 'text-blue-100' : 'text-green-700'}`}>
                          New customers only
                        </p>
                      )}
                      {billingPeriod === 'annual' && (
                        <p className={`text-sm mt-1 ${isPopular ? 'text-blue-200' : 'text-gray-600'}`}>
                          ${monthlyEquivalent.toFixed(2)}/month when billed annually
                        </p>
                      )}
                    </div>

                    <ul className="space-y-4 mb-8">
                      {plan.features.slice(0, 6).map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircleIcon className={`h-5 w-5 mr-3 mt-0.5 flex-shrink-0 ${
                            isPopular ? 'text-blue-200' : 'text-green-500'
                          }`} />
                          <span className={`text-sm ${isPopular ? 'text-blue-50' : 'text-gray-700'}`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                      {plan.features.length > 6 && (
                        <li>
                          <span className={`text-sm ${isPopular ? 'text-blue-200' : 'text-gray-400'}`}>
                            +{plan.features.length - 6} more features included
                          </span>
                        </li>
                      )}
                    </ul>

                    <Link 
                      href={isAuthenticated ? "/app/settings" : "/register"}
                      onClick={() => trackButtonClick("start_free_trial", `pricing_page_${plan.id}`)}
                      className={`block w-full text-center py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                        isPopular 
                          ? 'bg-white text-blue-600 hover:bg-gray-50 shadow-lg hover:shadow-xl' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                      }`}
                    >
                      Start 7-Day Free Trial
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Compare Features
            </h2>
            <p className="text-xl text-gray-600">
              See exactly what's included in each plan
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-6 px-6 font-semibold text-gray-900">Features</th>
                    <th className="text-center py-6 px-6 font-semibold text-gray-900">Starter</th>
                    <th className="text-center py-6 px-6 font-semibold text-gray-900 bg-blue-50">
                      Growth
                      <div className="text-xs font-normal text-blue-600 mt-1">Most Popular</div>
                    </th>
                    <th className="text-center py-6 px-6 font-semibold text-gray-900">Professional</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((category) => (
                    <React.Fragment key={category.category}>
                      <tr className="bg-gray-25">
                        <td colSpan={4} className="py-4 px-6 font-semibold text-gray-900 bg-gray-100">
                          {category.category}
                        </td>
                      </tr>
                      {category.items.map((item, itemIndex) => (
                        <tr key={itemIndex} className="border-t border-gray-100">
                          <td className="py-4 px-6 text-gray-700">{item.name}</td>
                          <td className="py-4 px-6 text-center">
                            {typeof item.starter === 'boolean' ? (
                              item.starter ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                              ) : (
                                <XMarkIcon className="h-5 w-5 text-gray-300 mx-auto" />
                              )
                            ) : (
                              <span className="text-gray-700">{item.starter}</span>
                            )}
                          </td>
                          <td className="py-4 px-6 text-center bg-blue-25">
                            {typeof item.growth === 'boolean' ? (
                              item.growth ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                              ) : (
                                <XMarkIcon className="h-5 w-5 text-gray-300 mx-auto" />
                              )
                            ) : (
                              <span className="text-gray-700">{item.growth}</span>
                            )}
                          </td>
                          <td className="py-4 px-6 text-center">
                            {typeof item.pro === 'boolean' ? (
                              item.pro ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                              ) : (
                                <XMarkIcon className="h-5 w-5 text-gray-300 mx-auto" />
                              )
                            ) : (
                              <span className="text-gray-700">{item.pro}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real Results from Real Businesses
            </h2>
            <p className="text-xl text-gray-600">
              See how much money our customers are making with Greet
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 p-8 rounded-2xl">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <span className="text-green-600 font-semibold">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-gray-600 text-sm">{testimonial.business}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{testimonial.savings}</div>
                    <div className="text-sm text-gray-600">additional revenue</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pricing Questions & Concerns
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing and guarantees
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <button
                  className="w-full px-6 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    const isOpening = openFaq !== index;
                    setOpenFaq(isOpening ? index : null);
                    if (isOpening) trackEvent('FAQ Opened', { question: faq.question, page: 'pricing' });
                  }}
                >
                  <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-6 text-gray-700 leading-relaxed transition-all duration-300 ${
                  openFaq === index ? 'pb-6 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Stop Missing Customers?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses already using Greet to capture every lead. 
            Start your free trial today
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick("start_free_trial", "pricing_page_cta_bottom")}
              className="bg-white text-blue-600 hover:bg-gray-50 px-10 py-4 rounded-xl font-bold text-lg transition-all hover:shadow-xl"
            >
              Start 7-Day Free Trial
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'pricing_page_cta_bottom')}
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Call Demo: (415) 877-3166
            </a>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 text-blue-200 text-sm">
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              7-day free trial
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Setup in 5 minutes
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Cancel anytime
            </div>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
